// The branches offered from the engineer's location, to show first on search
// (DEV-51045).
//
// Each branch in range is a checkbox row with its distance, nearest first;
// past three, the rest fold behind "View more (n)". One branch in range is
// ticked by default (AC1). Several start unticked, and any number can be
// ticked — a pill belongs to one supplier and branch, so guessing risks
// naming the merchant next door. Leaving everything unticked means none, so
// there is no separate "Neither" option.
//
// Deliberately departs from AC2's single-choice "Neither"-selected radios:
// "Show first" already takes several ticks, so the location can feed it
// several branches.
//
// It only ever suggests. Nothing is prioritised until the engineer confirms
// their location with the primary button.

import { useId, useState } from 'react'
import { Check } from 'lucide-react'

import { branchDisplayName } from '../../utils/branchPriority'
import type { BranchAtPosition } from '../../services/api'
import type { AtBranchSource } from '../../services/engineerSession'

// Branches shown before "View more (n)", as in the mock.
const NEARBY_BRANCHES_VISIBLE_LIMIT = 3

export interface AtBranchCardProps {
  /** Branches in range, nearest first. Renders nothing when empty. */
  branches: BranchAtPosition[]
  /**
   * How they were found. `device` knows where the engineer is and says so;
   * `postcode` only knows the area, so it says "near here" instead — a typed
   * postcode resolves to the centre of a postcode unit, not to a building.
   */
  source: AtBranchSource
  /** The branches ticked, any number of them. */
  selectedBranchIds: string[]
  onChange: (branchIds: string[]) => void
}

interface TickRowProps {
  label: string
  /** Shown after the label in grey, e.g. the distance. */
  detail?: string
  isChecked: boolean
  onToggle: () => void
}

/**
 * One selectable row with a checkbox, as in the mock: an empty grey-bordered
 * box when not chosen, filled with the brand colour and a white tick when
 * chosen — so chosen and not chosen never look alike.
 *
 * The row keeps the app's 44px minimum height so it stays easy to hit with a
 * thumb; there is no extra gap between rows.
 */
function TickRow({ label, detail, isChecked, onToggle }: TickRowProps) {
  return (
    <label className="flex min-h-11 cursor-pointer items-center gap-2.5 text-[13px] text-gray-800">
      <input
        type="checkbox"
        checked={isChecked}
        onChange={onToggle}
        className="peer sr-only"
      />
      <span
        aria-hidden="true"
        className={`grid h-5 w-5 shrink-0 place-items-center rounded border-2 peer-focus-visible:ring-2 peer-focus-visible:ring-brand-primary peer-focus-visible:ring-offset-2 ${
          isChecked
            ? 'border-brand-primary bg-brand-primary text-[var(--brand-primary-text)]'
            : 'border-gray-400 bg-white'
        }`}
      >
        {isChecked && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
      </span>
      <span className="min-w-0">
        {label}
        {detail && <span className="text-gray-500"> · {detail}</span>}
      </span>
    </label>
  )
}

/**
 * Two decimal places, e.g. "0.07 mi" — unlike the one decimal used elsewhere
 * in the app. Every branch here is within a quarter mile, and one decimal
 * rounds 0.25 up to "0.3 mi", which reads as outside the limit. The server
 * already rounds to two places after filtering on the exact distance, so this
 * can never show more than 0.25.
 */
function formatDistance(branch: BranchAtPosition): string {
  return `${branch.distanceMiles.toFixed(2)} mi`
}

export function AtBranchCard({
  branches,
  source,
  selectedBranchIds,
  onChange,
}: AtBranchCardProps) {
  const ids = useId()

  // "View more" starts folded for every new set of branches. Reset during
  // render (no effect) when the set changes, since the card stays mounted
  // between one location lookup and the next.
  const signature = branches.map((branch) => branch.branchId).join('|')
  const [expansion, setExpansion] = useState({ signature, isExpanded: false })
  if (expansion.signature !== signature) {
    setExpansion({ signature, isExpanded: false })
  }
  const isExpanded = expansion.signature === signature && expansion.isExpanded

  if (branches.length === 0) {
    return null
  }

  const toggle = (branchId: string) =>
    onChange(
      selectedBranchIds.includes(branchId)
        ? selectedBranchIds.filter((selectedId) => selectedId !== branchId)
        : [...selectedBranchIds, branchId],
    )

  // One layout for any number of branches: a heading, then each branch as a
  // tickable row with its distance. Only the heading changes with the count.
  const heading =
    branches.length === 1
      ? source === 'device'
        ? "You're at a branch"
        : 'Branch near here'
      : source === 'device'
        ? `You're near ${branches.length} branches`
        : `${branches.length} branches near here`

  const isMultiple = branches.length > 1
  const hasMore = branches.length > NEARBY_BRANCHES_VISIBLE_LIMIT
  // Nearest first, so folding keeps the closest in view.
  const visibleBranches = isExpanded
    ? branches
    : branches.slice(0, NEARBY_BRANCHES_VISIBLE_LIMIT)
  const selectedCount = branches.filter((branch) =>
    selectedBranchIds.includes(branch.branchId),
  ).length

  return (
    <fieldset
      aria-describedby={`${ids}-hint`}
      className="rounded-xl border border-gray-200 bg-gray-50 px-3 pt-2"
    >
      <legend className="float-left w-full text-sm font-semibold text-gray-900">
        {heading}
      </legend>
      {/* The mock shows this line for several branches. With one, the row
          speaks for itself, so the line is kept for screen readers only. */}
      <p
        id={`${ids}-hint`}
        className={isMultiple ? 'clear-both text-xs text-gray-600' : 'sr-only'}
      >
        Select branches to show their stock first
      </p>
      <div className="clear-both">
        {visibleBranches.map((branch) => (
          <TickRow
            key={branch.branchId}
            label={branchDisplayName(branch.supplierName, branch.branchName)}
            detail={formatDistance(branch)}
            isChecked={selectedBranchIds.includes(branch.branchId)}
            onToggle={() => toggle(branch.branchId)}
          />
        ))}
      </div>
      {hasMore && (
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setExpansion({ signature, isExpanded: !isExpanded })}
            aria-expanded={isExpanded}
            className="inline-flex min-h-11 items-center text-sm font-medium text-brand-primary hover:underline"
          >
            {isExpanded
              ? 'Show less'
              : `View more (${branches.length - NEARBY_BRANCHES_VISIBLE_LIMIT})`}
          </button>
          {/* Total ticked, so a choice folded away by "Show less" is never
              invisible: a count higher than the ticks in view gives it away.
              Plain text beside the link, not part of it. */}
          {selectedCount > 0 && (
            <span className="text-sm text-gray-500">
              · {selectedCount} selected
            </span>
          )}
        </div>
      )}
    </fieldset>
  )
}
